import { FC } from 'react';
import { Text, Button } from '@livechat/design-system-react-components';

import { Logo } from 'components/logo/Logo';
import {
  FREE_PRODUCTS,
  getAppLinkByName,
  getProductByName,
  getProductDisplayName,
} from 'helpers/product';
import Amplitude from 'services/Amplitude';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { saveItem } from 'helpers/storage/local-storage';
import { OrganizationGraphQLResponse, useOrganizations } from 'hooks/use-organizations';
import { useProducts } from 'hooks/use-products';
import {
  getAccountOrganizationProducts,
  accountProductsToDisplay,
  formatArray,
} from 'helpers/account';
import Location from 'services/Location';
import { parseJwt } from 'helpers/token';
import { useGetMe } from 'hooks/use-get-me';

import { OrganizationDetails } from '../organization-details/OrganizationDetails';

import * as styles from './styles';

type Props = {
  organizationsInWhichCanActivateProduct: OrganizationGraphQLResponse[];
  productToActivate: string;
};

export const ConfirmActivateProductContent: FC<Props> = ({
  organizationsInWhichCanActivateProduct,
  productToActivate,
}) => {
  const { email, roles } = useGetMe();
  const { organizations } = useOrganizations();
  const { getRedirectUrl, isFetchingProducts } = useProducts();

  const accountFilteredOrganizations = getAccountOrganizationProducts(organizations, roles);
  const state = Location.getHashParam('state') || Location.getUrlParam('state');
  const utmParams = Location.getUtmHashParams();
  const jwtParams = parseJwt(state);

  const handleStartTrialClick = () => {
    saveItem('signup', '1');
    pushExternalRedirectToDataLayer(getAppLinkByName(productToActivate));
    Amplitude.logEvent(email, 'SIGNUP_START_TRIAL_IN_ORG_CLICK', {}, true);
  };

  if (isFetchingProducts) {
    return null;
  }

  return (
    <div css={styles.container}>
      {organizationsInWhichCanActivateProduct &&
        organizationsInWhichCanActivateProduct.map((organization) => (
          <OrganizationDetails
            key={organization.organization_id}
            organizationName={organization.name}
            createdAt={organization.created_at}
          >
            <div css={styles.startTrialContainer}>
              <div css={styles.leftContainer}>
                <Logo size="xlarge" product={getProductByName(productToActivate)} />
                <div css={styles.startTrialTextContainer}>
                  <Text bold css={styles.startTrialText}>
                    {FREE_PRODUCTS.includes(productToActivate)
                      ? `Start ${getProductDisplayName(productToActivate)}`
                      : `Start ${getProductDisplayName(productToActivate)} trial`}
                  </Text>
                  <Text size="sm" css={styles.bottomText}>
                    {accountProductsToDisplay(organization, accountFilteredOrganizations).length > 0
                      ? `You use ${formatArray(
                          accountProductsToDisplay(organization, accountFilteredOrganizations)
                        )} in this
                    organization`
                      : "You don't have other products in this organization"}
                  </Text>
                </div>
              </div>
              <div css={styles.rightContainer}>
                <Button
                  css={styles.trialButton}
                  href={getRedirectUrl(productToActivate, organization.organization_id, {
                    jwtParams,
                    utmParams,
                  })}
                  onClick={handleStartTrialClick}
                >
                  {FREE_PRODUCTS.includes(productToActivate)
                    ? 'Start for free'
                    : 'Start free trial'}
                </Button>
              </div>
            </div>
          </OrganizationDetails>
        ))}
    </div>
  );
};
